import React, { useState, useEffect } from 'react';
import { 
  Layout, Menu, Upload, Button, message, Table, Empty, Space, Modal, Card, Row, Col, Input, Spin, Typography, Form, Alert
} from 'antd';
import { 
  UploadOutlined, HistoryOutlined, LogoutOutlined,
  EditOutlined, DeleteOutlined, FileTextOutlined,
  AudioOutlined, SettingOutlined, DollarOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { logout } from '../api/auth';

const { Header, Content, Footer } = Layout;

function Dashboard() {
  const { t,i18n } = useTranslation();
  const [subtitleFile, setSubtitleFile] = useState(null);
  const [rulesFile, setRulesFile] = useState(null);
  const [rules, setRules] = useState([]);
  const [rulesFilename, setRulesFilename] = useState(null);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [ruleContent, setRuleContent] = useState('');
  const [subtitles, setSubtitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [selectedSubtitleId, setSelectedSubtitleId] = useState(null);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();


  const handleAudioUpload = async () => {
    if (!audioFile) {
      message.warning(t('audio.selectFileFirst'));
      return;
    }

    const formData = new FormData();
    formData.append('audio', audioFile);

    try {
      const response = await fetch('/api/audio/transcribe', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('audio.transcribeSuccess'));
        setAudioFile(null);
        fetchSubtitles(); // 更新字幕列表
      } else {
        message.error(`${t('audio.transcribeFail')}: ${data.message || t('common.unknownError')}`);
      }
    } catch (error) {
      console.error('转录错误:', error);
      message.error(t('audio.transcribeError'));
    }
  };

  // 修改规则表格列定义
  const ruleColumns = [
    {
      title: t('rule.filename'),
      dataIndex: 'filename',
      key: 'filename',
      render: (text, record) => (
        editingName === record.id ? (
          <Input
            defaultValue={text}
            onPressEnter={(e) => handleRuleRename(record.id, e.target.value)}
            onBlur={() => setEditingName(null)}
            autoFocus
          />
        ) : (
          <span 
            style={{ cursor: 'pointer' }}
            onClick={() => setEditingName(record.id)}
          >
            {text}
          </span>
        )
      )
    },
    {
      title: t('title.action'),
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => handleEditRule(record)}
            icon={<EditOutlined />}
          >
            {t('common.edit')}
          </Button>
          <Button 
            type="link" 
            danger
            onClick={() => handleDeleteRule(record.id)}
            icon={<DeleteOutlined />}
          >
            {t('title.delete')}
          </Button>
          <Button
            type="link"
            onClick={() => handleApplyRule(record.id)}
            disabled={!selectedSubtitleId}
          >
            {selectedSubtitleId ? t('rule.apply') : t('subtitle.selectFileFirst')}
          </Button>
        </Space>
      ),
    },
  ];

  // 重命名规则
  const handleRuleRename = async (id, newName) => {
    try {
      const response = await fetch(`/api/subtitles/rules/${id}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ filename: newName }),
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('rule.renameSuccess'));
        setEditingName(null);
        fetchRules();
      } else {
        message.error(t('rule.renameFail'));
      }
    } catch (error) {
      console.error('更新规则名称错误:', error);
      message.error(t('rule.renameFail'));
    }
  };

  // 编辑规则
  const handleEditRule = (rule) => {
    setRuleContent(
      rule.rules
        .map(r => `${r.find},${r.replace}`)
        .join('\n')
    );
    setEditingRuleId(rule.id); // 设置正在编辑的规则 ID
    setShowRuleEditor(true);
  };

  // 删除规则
  const handleDeleteRule = async (id) => {
    try {
      const response = await fetch(`/api/subtitles/rules/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('rule.deleteSuccess'));
        fetchRules();
      } else {
        message.error(t('rule.deleteFail'));
      }
    } catch (error) {
      console.error('删除规则错误:', error);
      message.error(t('rule.deleteFail'));
    }
  };

  // 应用规则
  const handleApplyRule = async (ruleId) => {
    if (!selectedSubtitleId) {
      message.warning(t('subtitle.selectFileFirst'));
      return;
    }

    try {
      const response = await fetch('/api/subtitles/replace', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          subtitleId: selectedSubtitleId,
          ruleId: ruleId
        }),
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('rule.applySuccess'));
        // 导航到编辑器页面
        navigate(`/editor/${selectedSubtitleId}`);
      } else {
        message.error(data.message || t('rule.applyFail'));
      }
    } catch (error) {
      console.error('应用规则错误:', error);
      message.error(t('rule.applyFail'));
    }
  };

  // 获取字幕列表
  const fetchSubtitles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/subtitles/list', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setSubtitles(data.subtitles);
      } else {
        message.error(t('subtitle.fetchFail'));
      }
    } catch (error) {
      console.error('获取字幕列表错误:', error);
      message.error(t('subtitle.fetchFail'));
    } finally {
      setLoading(false);
    }
  };

  // 获取当前规则
  const fetchRules = async () => {
    try {
      setRulesLoading(true);
      const response = await fetch('/api/subtitles/rules', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setRules(data.rules); // 直接使用返回的规则数组
        // 不再需要设置 rulesFilename，因为文件名现在包含在每个规则对象中
      } else {
        message.error(t('rule.fetchFail'));
      }
    } catch (error) {
      console.error('获取规则失败:', error);
      message.error(t('rule.fetchFail'));
    } finally {
      setRulesLoading(false);
    }
  };

  useEffect(() => {
    fetchSubtitles();
    fetchRules();
  }, []);

  const handleSubtitleUpload = async () => {
    if (!subtitleFile) {
      message.warning(t('subtitle.selectFileFirst'));
      return;
    }

    if (subtitleFile.size === 0) {
      message.error(t('subtitle.emptyFile'));
      return;
    }

    const formData = new FormData();
    formData.append('subtitle', subtitleFile);

    try {
      const response = await fetch('/api/subtitles/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        message.success(t('subtitle.uploadSuccess'));
        setSubtitleFile(null);
        fetchSubtitles();
      } else {
        if (data.code === 'QUOTA_EXCEEDED') {
          message.error(t('subtitle.quotaExceeded'));
        } else {
          message.error(`${t('subtitle.uploadFail')}: ${data.message || t('common.unknownError')}`);
        }
      }
    } catch (error) {
      console.error('上传错误:', error);
      message.error(t('subtitle.uploadError'));
    }
  };

  const handleRulesUpload = async () => {
    if (!rulesFile) {
      message.warning(t('rule.selectFileFirst'));
      return;
    }

    const formData = new FormData();
    formData.append('rules', rulesFile);

    try {
      const response = await fetch('/api/subtitles/upload-rules', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('rule.uploadSuccess'));
        setRulesFile(null);
        fetchRules();
      } else {
        message.error(`${t('rule.uploadFail')}: ${data.message}`);
      }
    } catch (error) {
      console.error('上传错误:', error);
      message.error(t('subtitle.uploadError'));
    }
  };

  // 添加字幕重命名函数
  const handleSubtitleRename = async (id, newName) => {
    try {
      const response = await fetch(`/api/subtitles/${id}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ filename: newName }),
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('subtitle.renameSuccess'));
        setEditingName(null);
        fetchSubtitles();
      } else {
        message.error(t('common.deleteFail'));
      }
    } catch (error) {
      console.error('更新文件名错误:', error);
      message.error(t('common.deleteFail'));
    }
  };

  // 添加 handleLogout 函数
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('退出失败:', error);
      message.error(t('common.logoutFail'));
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: t('common.confirmDelete'),
      content: t('subtitle.confirmDeleteMsg'),
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await fetch(`/api/subtitles/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            message.success(t('common.deleteSuccess'));
            fetchSubtitles();
          } else {
            message.error(t('common.deleteFail'));
          }
        } catch (error) {
          console.error('删除错误:', error);
          message.error(t('subtitle.deleteError'));
        }
      },
    });
  };

  const columns = [
    {
      title: t('subtitle.filename'),
      dataIndex: 'filename',
      key: 'filename',
      render: (text, record) => (
        editingName === record._id ? (
          <Input
            defaultValue={text}
            onPressEnter={(e) => handleSubtitleRename(record._id, e.target.value)}
            onBlur={() => setEditingName(null)}
            autoFocus
          />
        ) : (
          <span 
            style={{ cursor: 'pointer' }}
            onClick={() => setEditingName(record._id)}
          >
            <FileTextOutlined /> {text}
          </span>
        )
      ),
    },
    {
      title: t('dashboard.uploadTime'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: t('title.action'),
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => navigate(`/editor/${record._id}`)}
          >
            {t('common.edit')}
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          >
            {t('title.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const menuItems = [
    { label: t('menu.history'), key: 'history', icon: <HistoryOutlined /> },
    { label: t('menu.logout'), key: 'logout', icon: <LogoutOutlined /> },
  ];

  // 修改 handleMenuClick 函数
  const handleMenuClick = async ({ key }) => {
    if (key === 'history') {
      navigate('/history');
    } else if (key === 'logout') {
      try {
        await logout();
        navigate('/');
      } catch (error) {
        console.error('退出失败:', error);
        message.error(t('common.logoutFail'));
      }
    }
  };

  return (
    <Layout className="dashboard-layout">
      <Header style={{ background: '#001529', padding: '0 24px' }}>
        <div style={{ 
          float: 'left', 
          color: '#fff', 
          fontSize: '20px',
          fontWeight: 'bold'
        }}>
          {t('dashboard.title')}
        </div>
        
        <div style={{float:'right',display:'flex',alignItems:'center',gap:8}}>
          <Menu
          theme="dark"
          mode="horizontal"
          items={[
            { key: 'audio', label: t('menu.audio'), icon: <AudioOutlined /> },
            { key: 'subtitle', label: t('menu.subtitle'), icon: <FileTextOutlined /> },
            { key: 'rules', label: t('menu.rules'), icon: <SettingOutlined /> },
            { key: 'history', label: t('menu.history'), icon: <HistoryOutlined /> },
            { key: 'plan', label: t('menu.plan'), icon: <DollarOutlined /> },
            { key: 'logout', label: t('menu.logout'), icon: <LogoutOutlined /> }
          ]}
          onClick={({ key }) => {
            switch(key) {
              case 'audio':
                navigate('/audio-manager');
                break;
              case 'subtitle':
                navigate('/subtitle-manager');
                break;
              case 'rules':
                navigate('/rule-manager');
                break;
              case 'history':
                navigate('/history');
                break;
              case 'plan':
                navigate('/plan');
                break;
              case 'logout':
                handleLogout();
                break;
            }
          }}
           style={{ background:'#001529' }}
        />
        <LanguageSelector style={{ marginLeft: 'auto' }}/>
        </div>
      </Header>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* audio transcription section */}
          <Row style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <AudioOutlined style={{ fontSize: '24px' }} />
                    <Typography.Title level={3} style={{ margin: 0 }}>{t('audio.title')}</Typography.Title>
                  </Space>
                } 
                bordered={false}
                className="audio-upload-card"
              >
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  padding: '40px 0'
                }}>
                  <Upload
                    accept="audio/*"
                    beforeUpload={(file) => {
                      setAudioFile(file);
                      return false;
                    }}
                    fileList={audioFile ? [audioFile] : []}
                    maxCount={1}
                    onRemove={() => {
                      setAudioFile(null);
                      return true;
                    }}
                  >
                    <Button icon={<UploadOutlined />} size="large">
                      {t('audio.selectFile')}
                    </Button>
                  </Upload>
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleAudioUpload} 
                    loading={uploading}
                    disabled={!audioFile}
                    style={{ marginTop: '20px', minWidth: '200px' }}
                  >
                    {t('audio.uploadAndTranscribe')}
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
          <Row gutter={24} style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Card 
                title={
                  <Space>
                    <FileTextOutlined />
                    {t('subtitle.uploadTitle')}
                  </Space>
                } 
                bordered={false}
              >
                <Upload
                  beforeUpload={(file) => {
                    setSubtitleFile(file);
                    return false;
                  }}
                  fileList={subtitleFile ? [subtitleFile] : []}
                  onRemove={() => {
                    setSubtitleFile(null);
                    return true;
                  }}
                >
                  <Button icon={<UploadOutlined />}>{t('subtitle.chooseFile')}</Button>
                </Upload>
                <Button 
                  type="primary" 
                  onClick={handleSubtitleUpload} 
                  style={{ marginTop: '10px' }}
                >
                  {t('subtitle.uploadButton')}
                </Button>
              </Card>
            </Col>
            <Col span={12}>
              <Card 
                title={
                  <Space>
                    <SettingOutlined />
                    {t('rule.uploadTitle')}
                  </Space>
                } 
                bordered={false}
              >
                <Upload
                  beforeUpload={(file) => {
                    const isTxtOrJson = file.type === 'text/plain' || file.type === 'application/json';
                    if (!isTxtOrJson) {
                      message.error(t('rule.uploadError'));
                      return false;
                    }
                    setRulesFile(file);
                    return false;
                  }}
                  accept=".txt,.json"
                  fileList={rulesFile ? [rulesFile] : []}
                >
                  <Button icon={<UploadOutlined />}>{t('rule.selectFile')}</Button>
                </Upload>
                <Button 
                  type="primary" 
                  onClick={handleRulesUpload} 
                  style={{ marginTop: '10px' }}
                >
                  {t('rule.uploadButton')}
                </Button>
              </Card>
            </Col>
          </Row>

          <Card 
            title={t('dashboard.latestSubtitles')} 
            extra={<Button type="link" onClick={() => navigate('/subtitle-manager')}>{t('common.viewAll')}</Button>}
            style={{ marginBottom: '24px' }}
          >
            <Table 
              columns={columns} 
              dataSource={subtitles}
              rowKey="_id"
              loading={loading}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedSubtitleId ? [selectedSubtitleId] : [],
                onChange: (selectedRowKeys) => {
                  setSelectedSubtitleId(selectedRowKeys[0]);
                },
                columnTitle: t('common.select'),
                columnWidth: 60,
              }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `${t('common.total')} ${total} ${t('common.items')}`
              }}
            />
            {selectedSubtitleId && (
              <div style={{ marginTop: 16, color: '#666' }}>
                {t('dashboard.selectedSubtitle')}
              </div>
            )}
          </Card>

          {/* 替换原有的规则显示部分 */}
          <Card 
            title={
              <Space>
                {t('rule.management')}
                {rulesLoading && <Spin size="small" />}
              </Space>
            }
            bordered={false}
            style={{ marginTop: '24px' }}
            extra={
              <Button 
                type="primary" 
                onClick={() => {
                  setRuleContent('');
                  setShowRuleEditor(true);
                }}
              >
                {t('rule.create')}
              </Button>
            }
          >
            <Table
              loading={rulesLoading}
              dataSource={rules}
              columns={ruleColumns}
              pagination={false}
              size="small"
              bordered
              rowKey="id"
            />
          </Card>
          
            <Modal
              title={t('rule.editorTitle')}
              open={showRuleEditor}
              onOk={async () => {
                try {
                  const ruleLines = ruleContent.trim().split('\n');
                  const invalidLines = ruleLines.filter(line => {
                    const parts = line.trim().split(',');
                    return parts.length !== 2 || !parts[0].trim() || !parts[1].trim();
                  });

                  if (invalidLines.length > 0) {
                    message.error(t('rule.editorError'));
                    return;
                  }

                  const formData = new FormData();
                  const blob = new Blob([ruleContent], { type: 'text/plain' });
                  formData.append('rules', blob, 'rules.txt');

                  // 如果是编辑现有规则，添加规则ID
                  if (editingRuleId) {
                    formData.append('ruleId', editingRuleId);
                  }

                  const response = await fetch('/api/subtitles/upload-rules', {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                  });

                  const data = await response.json();
                  if (data.success) {
                    message.success(editingRuleId ? t('rule.editorUpdateSuccess') : t('rule.editorSaveSuccess'));
                    setShowRuleEditor(false);
                    setRuleContent('');
                    setEditingRuleId(null); // 清除编辑状态
                    fetchRules();
                  } else {
                    message.error(data.message || t('rule.editorSaveFail'));
                  }
                } catch (error) {
                  console.error('保存规则错误:', error);
                  message.error(t('rule.editorSaveFail'));
                }
              }}
              onCancel={() => {
                setShowRuleEditor(false);
                setRuleContent('');
                setEditingRuleId(null); // 清除编辑状态
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <p>{t('rule.editorInstructions')}</p>
                <p>{t('rule.editorExample')}</p>
                <pre style={{ background: '#f5f5f5', padding: 8 }}>
                  ブランド,brand{'\n'}
                  商品,product{'\n'}
                  会社,company
                </pre>
              </div>
              <Input.TextArea
                value={ruleContent}
                onChange={e => setRuleContent(e.target.value)}
                placeholder={t('rule.editorPlaceholder')}
                rows={10}
                onPaste={(e) => {
                  // 处理从 Excel 粘贴的内容
                  const clipboardData = e.clipboardData;
                  const pastedData = clipboardData.getData('text');
                  // 如果包含制表符，转换为逗号
                  if (pastedData.includes('\t')) {
                    e.preventDefault();
                    const convertedData = pastedData
                      .split('\n')
                      .map(line => line.replace('\t', ','))
                      .join('\n');
                    setRuleContent(convertedData);
                  }
                }}
              />
              <div style={{ marginTop: 8, color: '#666' }}>
                {t('rule.editorTip')}
              </div>
            </Modal>
        </div>
      </Content>
      <Footer style={{ textAlign: 'center' }}>
        {t('dashboard.copyright')}
      </Footer>
    </Layout>
  );
}

export default Dashboard;